import { WindowTitle } from "@saleor/components/WindowTitle";
import useNavigator from "@saleor/hooks/useNavigator";
import useNotifier from "@saleor/hooks/useNotifier";
import { findValueInEnum, maybe } from "@saleor/misc";
import { StoreTypeEnum } from "@saleor/types/globalTypes";
import createMetadataCreateHandler from "@saleor/utils/handlers/metadataCreateHandler";
import {
  useMetadataUpdate,
  usePrivateMetadataUpdate
} from "@saleor/utils/metadata/updateMetadata";
import React from "react";
import { useIntl } from "react-intl";

import CategoryCreatePage, { FormData } from "../components/CategoryCreatePage";
import { useCategoryCreateMutation } from "../mutations";
import { useCategoryDetailsQuery } from "../queries";
import { CategoryCreate } from "../types/CategoryCreate";
import { categoryListUrl, categoryUrl } from "../urls";

interface CategoryCreateViewProps {
  parentId: string;
}

export const CategoryCreateView: React.FC<CategoryCreateViewProps> = ({
  parentId
}) => {
  const navigate = useNavigator();
  const notify = useNotifier();
  const intl = useIntl();
  const [updateMetadata] = useMetadataUpdate({});
  const [updatePrivateMetadata] = usePrivateMetadataUpdate({});
  const [imageToUpload, setImageToUpload] = React.useState(null);
  let storeType = null;

  if (parentId) {
    const { data } = useCategoryDetailsQuery({
      displayLoader: true,
      variables: { first: 10, id: parentId }
    });
    storeType = data?.category.storeType;
  }

  const handleSuccess = (data: CategoryCreate) => {
    if (data.categoryCreate.errors.length === 0) {
      notify({
        status: "success",
        text: intl.formatMessage({
          defaultMessage: "Category created"
        })
      });
      navigate(categoryUrl(data.categoryCreate.category.id));
    } else if (
      data.categoryCreate.errors.some(e => e.code === "INVALID") &&
      data.categoryCreate.errors.some(e => e.field === "iconImage")
    ) {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage:
            "Header icon resolution should not exceed 500x500 pixels"
        })
      });
    } else {
      notify({
        status: "error",
        text: intl.formatMessage({
          defaultMessage: "There's an error!"
        })
      });
    }
  };

  const [createCategory, createCategoryResult] = useCategoryCreateMutation({
    onCompleted: handleSuccess
  });
  const handleCreate = async (formData: FormData) => {
    const result = await createCategory({
      variables: {
        input: {
          color: formData.color,
          descriptionJson: JSON.stringify(formData.description),
          iconImage: imageToUpload,
          name: formData.name,
          seo: {
            description: formData.seoDescription,
            title: formData.seoTitle
          },
          slug: formData.slug,
          smMarketsId: formData.smMarketsId,
          storeType: maybe(() =>
            findValueInEnum(formData.storeType, StoreTypeEnum)
          )
        },
        parent: parentId || null
      }
    });

    return result.data?.categoryCreate.category?.id || null;
  };
  const handleSubmit = createMetadataCreateHandler(
    handleCreate,
    updateMetadata,
    updatePrivateMetadata
  );

  return (
    <>
      <WindowTitle
        title={intl.formatMessage({
          defaultMessage: "Create Category",
          description: "window title"
        })}
      />

      <CategoryCreatePage
        saveButtonBarState={createCategoryResult.status}
        errors={createCategoryResult.data?.categoryCreate.errors || []}
        disabled={createCategoryResult.loading}
        onIconUpload={setImageToUpload}
        storeType={storeType}
        onBack={() =>
          navigate(parentId ? categoryUrl(parentId) : categoryListUrl())
        }
        onSubmit={handleSubmit}
      />
    </>
  );
};
export default CategoryCreateView;
